import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { inLandscape, inList } from "@app/components/report-document/editor/utils";
import notify from "@app/components/notify";

function handlePasteText(value) {
    value = sanitize(value);
    return value;
}

function handlePasteHtml(value, editor) {
    value = sanitize(value);
    value = sanitizeWordEmptyFields(value);
    value = sanitizeImage(value, editor);
    value = sanitizeTable(value, editor);

    return value;
}

function sanitize(value) {
    /* eslint-disable no-control-regex */
    value = value.replace(/\u0002/gi, "-");
    value = value.replace(/[\u0001-\u001f]/gi, (char) => {
        // Preserve newline characters
        if (char === "\n" || char === "\r") {
            return char;
        }
        return " ";
    });
    value = value.replace(/[\u0080-\u009F]/gi, " ");

    return value;
}

function sanitizeWordEmptyFields(html) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;

    const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_COMMENT);
    const emptyFieldComments = [];

    let node;
    while ((node = walker.nextNode())) {
        const value = node.nodeValue || "";
        if (
            value.includes("supportFields") &&
            value.includes("field-begin") &&
            value.includes("field-separator")
        ) {
            // Extract text between field-begin closing tag and field-separator opening tag
            const match = value.match(/field-begin[^>]*>([\s\S]*?)<span[^>]*field-separator/);
            const instruction = match ? match[1].replace(/<[^>]+>/g, "").trim() : null;
            if (instruction === "") {
                emptyFieldComments.push(node);
            }
        }
    }

    for (const beginComment of emptyFieldComments) {
        let sibling = beginComment.nextSibling;
        if (beginComment.parentNode) {
            beginComment.parentNode.removeChild(beginComment);
        }

        while (sibling) {
            const next = sibling.nextSibling;
            const isFieldEnd =
                sibling.nodeType === Node.COMMENT_NODE &&
                (sibling.nodeValue || "").includes("field-end");
            if (sibling.parentNode) {
                sibling.parentNode.removeChild(sibling);
            }
            sibling = next;
            if (isFieldEnd) {
                break;
            }
        }
    }

    // Strip MsoBookmark wrappers (Word cross-reference tracking spans, not needed in app)
    wrapper.querySelectorAll("span.MsoBookmark").forEach((span) => {
        while (span.firstChild) {
            span.parentNode.insertBefore(span.firstChild, span);
        }
        span.remove();
    });

    return wrapper.innerHTML;
}

function sanitizeImage(html, editor) {
    // Detect VML elements (flowcharts and shapes from Word/Office) before ProseMirror silently drops them
    const hasVmlElements = /<v:[a-z]/i.test(html);
    let hasRemovedUnsupported = hasVmlElements;

    if (html.includes("<img")) {
        if (inList(editor)) {
            // handle gap cursor
            setTimeout(() => {
                const cursor = editor.state.tr.selection.$cursor;
                if (!cursor) {
                    editor.chain().focus().createParagraphNear().insertContent(" ").focus().run();
                }
            }, 100);
        }

        // Validate images in the pasted content
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const images = tempDiv.querySelectorAll("img");

        images.forEach((img) => {
            const src = img.getAttribute("src") || "";
            const outerHtml = img.outerHTML || "";

            // Check if it's a data URL that's not an image
            if (src.startsWith("data:") && !src.startsWith("data:image/")) {
                img.remove();
                hasRemovedUnsupported = true;
            } else if (outerHtml.includes("v:") || outerHtml.includes("xmlns:v=")) {
                // Remove VML/Word shapes (e.g. flowcharts)
                img.remove();
                hasRemovedUnsupported = true;
            }
        });

        html = tempDiv.innerHTML;

        // const cursor = editor.state.tr.selection.$cursor;
        // const parentDepth = inLandscape(editor) ? 2 : 1;
        // const notRootLevel = cursor?.depth > parentDepth;

        // if (notRootLevel) {
        //     // retrieve the parent node pos and create a paragraph after it
        //     const parentNode = cursor.node(parentDepth);
        //     const nodePos = editor.commands.getNodePos(parentNode);
        //     if (nodePos) {
        //         editor
        //             .chain()
        //             .focus()
        //             .insertContentAt(nodePos.to, "</p>")
        //             .setTextSelection(nodePos.to)
        //             .run();
        //     }
        // }
    }

    if (hasRemovedUnsupported) {
        setTimeout(() => {
            notify.error(
                "Unsupported content: This item cannot be pasted. Please try saving it as a supported image format first.",
            );
        }, 0);
    }

    return html;
}

function inTableCell(editor) {
    return editor.state.selection?.$head.path.some((node) =>
        ["tableCell", "tableHeader"].includes(node?.type?.name),
    );
}

function unwrapSingleCellTables(wrapper) {
    for (const table of wrapper.querySelectorAll("table")) {
        const rows = table.querySelectorAll("tr");
        const cells = table.querySelectorAll("td, th");

        if (rows.length !== 1 || cells.length !== 1) {
            continue;
        }

        table.replaceWith(...cells[0].childNodes);
    }
}

function sanitizeTable(html, editor) {
    if (html.includes("</tr>")) {
        // // Convert valign="value" to style="vertical-align: value"
        // html = html.replace(/valign="(top|middle|bottom)"/gi, (match, p1) => {
        //     return `style="vertical-align: ${p1 || "middle"}"`;
        // });

        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;

        if (inTableCell(editor)) {
            unwrapSingleCellTables(wrapper);
        }

        // only a real table can't nest, so it has to go after the current one
        if (wrapper.querySelector("table")) {
            const cursor = editor.state.tr.selection.$cursor;
            const parentDepth = inLandscape(editor) ? 2 : 1;
            const notRootLevel = cursor?.depth > parentDepth;

            if (notRootLevel) {
                // retrieve the parent node pos and create a paragraph after it
                const parentNode = cursor.node(parentDepth);
                const nodePos = editor.commands.getNodePos(parentNode);
                if (nodePos) {
                    editor
                        .chain()
                        .focus()
                        .insertContentAt(nodePos.to, "</p>")
                        .setTextSelection(nodePos.to)
                        .run();
                }
            }
        }

        html = wrapper.innerHTML;

        const pasteFromWord = html.match(/WordDocument/);
        const tds = wrapper.querySelectorAll("td");

        for (const td of tds) {
            if (td.getAttribute("valign")) {
                td.style.verticalAlign = td.getAttribute("valign");
            } else if (pasteFromWord) {
                const styles = window.getComputedStyle(td);
                if (!styles.verticalAlign) {
                    td.style.verticalAlign = "middle";
                }
            }
        }

        const tables = wrapper.querySelectorAll("table");
        for (const table of tables) {
            const tbody = table.querySelector("tbody");
            const rows = Array.from(tbody.childNodes).filter((node) => node.nodeType === 1); // Leave only element nodes

            // remove empty rows
            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                const next = rows[i + 1];

                // reset the rowspan if the next row is empty
                if (!next || !next.childNodes.length) {
                    const cells = row.querySelectorAll("td");
                    for (const cell of cells) {
                        cell.removeAttribute("rowspan");
                    }
                }

                if (!row.childNodes.length) {
                    tbody.removeChild(row);
                }
            }
        }

        html = wrapper.innerHTML;

        const hasContentBeyondATable =
            html
                .replace(/<!--.*?-->/g, "") // remove comments
                .replace(/<table.*?>.*?<\/table>/g, "") // remove tables
                .replace(/<[^>]*>/g, "") // remove tags
                .trim().length > 0; // check if there is any content

        if (!hasContentBeyondATable) {
            // replace the slice with a correct node type
            html = html.replace(/data-pm-slice="(\d+) \d+ \[\]"/g, 'data-pm-slice="1 1 []"');
        }
    }

    return html;
}

export default Extension.create({
    name: "pasteExtension",

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey("transformPasted"),
                props: {
                    transformPastedText: handlePasteText,
                    transformPastedHTML: (value) => handlePasteHtml(value, this.editor),
                },
            }),
        ];
    },
});
